/**
 * The lived record, as the three answers the sheet's live variant renders from: where the client
 * stands, what each consumed cell was given and did, and how the backend judged each cycle.
 *
 * ── THE VERDICT IS READ, NEVER COMPUTED, AND THAT IS THE WHOLE POINT ────────────────────────
 * `PreviousCountedSet.Shortfalls` decides both what the sheet draws and what the next cycle
 * prescribes, on the backend, from the rows frozen onto the occurrence (R12, ADR
 * `2026-09-15-executions-ride-on-the-full-read`). This module therefore reads TWO wire facts and no
 * numbers at all: the occurrence's `status` and its `delivered` flag. A comparison of an actual
 * against a prescription anywhere below would be a second author, and the two would then tell a
 * trainer different things about the same cycle — the sheet saying "delivered" over a cycle the
 * engine refused the step for.
 *
 * `delivery-verdict-parity.json` is the backend's own emitted fixture and
 * `executionsToMatrixInputs.test.ts` replays it, so that rule is a red build rather than a comment.
 *
 * ── The SHORTFALL is the set's, not the column's ────────────────────────────────────────────
 * A row short on repetitions holds the whole exercise (R3), whichever column the grid happens to be
 * drawn on. So a lived cell is marked short when its SET fell short on any judged column, and the
 * cell's accessible name says which — „brak na powtórzeniach" under a kilogram cell is the honest
 * reading, and a green kilogram cell inside a refused cycle is not.
 *
 * ── A warm-up is in `sets[]` and outside the verdict ────────────────────────────────────────
 * The backend ships it so the rows line up with `templateSets`; it carries no shortfall and buys no
 * step. Nothing here filters it out, because the grid needs the row — `countsAsWork` is what the
 * renderer already asks, and a second copy of the counting rule is exactly what
 * `setCounting.ts` exists to prevent.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { SlotExecution, SlotExecutionSet } from '~/entities/plan-draft';
import type { MeasurementFieldKey } from '~/shared/measurement';

/**
 * How one lived cycle ended, in the four readings a trainer's sheet distinguishes.
 *
 * Derived from `status` and `delivered` and from nothing else:
 *
 * · `missed` — the client let the occurrence pass. There is a frozen prescription and no work.
 * · `skipped`, the client declined it. Same shape, different reason, and the copy differs.
 * · `cancelled`, the session was called off. Not the client's doing, which is why it is the one
 *   untrained reading that stays neutral: owner's call, 15 September 2026, made a missed or skipped
 *   session a FAILURE drawn red, and a cancelled one is not something the client failed.
 * · `inProgress` — the client is standing in it right now. It is NOT short: the backend's
 *   `delivered` is false on an unfinished occurrence because nothing has been ticked yet, and
 *   drawing a red ✗ over every set of the session somebody is in the middle of would tell a trainer
 *   their client failed a workout they have not finished. The reading comes off the STATUS, which is
 *   a wire fact like the other three; nothing here looks at a number to reach it.
 * · `delivered` — the backend's own yes: every counted row ticked and every judged column reached.
 * · `short` — its no. One un-ticked set is enough, and so is one column left unreached.
 * · `unjudged` — the wire carried no flag at all. It is NOT „short": a verdict this app invents is
 *   the one thing this module exists to prevent, and an optional field an older server leaves out
 *   would otherwise draw a red ✗ over a cycle nobody refused. It is drawn like a cycle nobody
 *   trained — neutral, with no claim on it.
 */
export type CycleVerdict =
  | 'delivered'
  | 'short'
  | 'missed'
  | 'inProgress'
  | 'skipped'
  | 'cancelled'
  | 'unjudged';

/** One consumed cell: what the client was given there, what they did, and whether it counted. */
export interface LoggedCell {
  /** The prescription FROZEN at start (R2) — never the template's current cell. */
  readonly prescribed: Partial<Record<MeasurementFieldKey, number>>;
  /** What the client logged. Empty on a missed or declined occurrence. */
  readonly actual: Partial<Record<MeasurementFieldKey, number>>;
  /** Whether the client ticked the row off. */
  readonly done: boolean;
  /** The columns this row was judged on and did not reach. Empty for a row that met every bar. */
  readonly shortfall: ReadonlySet<MeasurementFieldKey>;
  /** The set's role as the occurrence froze it, verbatim — a warm-up is in the list and unjudged. */
  readonly setType: string;
  /**
   * The banded column's upper end as the occurrence FROZE it, or `null` where the rule banded
   * nothing. The plot's lived label prints „× 8–12" from it — the range the client was actually
   * asked to climb, rather than the one the trainer has since authored.
   */
  readonly ceiling: number | null;
}

/** Everything the live variant of the sheet needs, and nothing it could compute wrongly. */
export interface LiveMatrixInput {
  /**
   * The NEXT cycle this exercise will be performed in, the column the mock's „TU JEST KLIENT" tag
   * sits over, and one a trainer can still pin. `null` where the exercise has no such cycle left in
   * its lifespan. See `nextCycleToPerform`.
   */
  readonly nowCycle: number | null;
  /** Cycles `1..this` are behind the client plan-wide. The fallback lock where `closedCycles` is unknown. */
  readonly consumedCycles: number;
  /**
   * The cycles the server says an edit can no longer reach on this exercise's DAY, or `null` where
   * the read did not say. When known it is the lock, because it is what the save judges by.
   */
  readonly closedCycles: ReadonlySet<number> | null;
  /** `"<cycle>:<setIndex>"` → the cell. A cycle the client lived with no row for a set has none. */
  readonly logged: ReadonlyMap<string, LoggedCell>;
  /** One reading per lived cycle. A cycle with no occurrence is ABSENT rather than `delivered`. */
  readonly verdicts: ReadonlyMap<number, CycleVerdict>;
}

/** The one address both writers and readers of `logged` use. */
export function loggedCellKey(cycleIndex: number, setIndex: number): string {
  return `${String(cycleIndex)}:${String(setIndex)}`;
}

/**
 * The statuses that answer the verdict on their own, before `delivered` is read.
 *
 * Read off the assigned-plan view's own vocabulary and kept as a table rather than a negation of
 * the finished ones, because a status a newer server adds must not silently become „bez treningu":
 * anything unrecognised falls through to the `delivered` flag, which the backend always sets.
 */
const BY_STATUS: Readonly<Record<string, CycleVerdict>> = {
  Missed: 'missed',
  Cancelled: 'cancelled',
  Skipped: 'skipped',
  InProgress: 'inProgress',
};

/** How one lived cycle ended — `status` first, then the backend's own verdict. Never a number. */
function verdictOf(execution: SlotExecution): CycleVerdict {
  const byStatus = BY_STATUS[execution.status];
  if (byStatus !== undefined) return byStatus;
  if (execution.delivered === null) return 'unjudged';
  return execution.delivered ? 'delivered' : 'short';
}

function cellOf(set: SlotExecutionSet): LoggedCell {
  return {
    prescribed: set.prescribed,
    actual: set.actual,
    done: set.isCompleted,
    shortfall: new Set(set.shortfallFields),
    setType: set.setType,
    ceiling: set.prescribedCeiling,
  };
}

export interface LiveMatrixSource {
  /** `ExerciseDraft.executions` — `null` outside a live fork, `[]` live with nothing lived yet. */
  readonly executions: readonly SlotExecution[] | null;
  /** The exercise's lifespan, ascending, the cycles the grid draws a column for. */
  readonly lifespan: readonly number[];
  /** `DayDraft.liveClosedCycles` of the day holding the exercise. */
  readonly closedCycles: readonly number[] | null;
  /** `PlanLiveContext.lastConsumedCycle`, or the count `consumedCycleCount` already resolved. */
  readonly consumedCycles: number;
}

/**
 * Whether a cycle takes no edit, the ONE lock both the grid's cells and the now column read.
 *
 * ── The server's per-day stamp, not the plan-wide prefix ────────────────────────────────────
 * `PlanTemplateProcessor.StampCyclesTheEditCanNoLongerReach` puts `FrozenPlanHistory.IsClosed` on
 * every day of the `/full` read: a cycle is closed for a day when that day was lived there, or when
 * nothing of the day was ever scheduled there and the cycle is inside the lived prefix. A day whose
 * session in a consumed cycle is still UPCOMING is open, because regeneration re-stamps that workout
 * from the template on save, so a pin there reaches the client. The plan-wide `consumedCycles` lock
 * refused exactly that cell (owner QA, 15 September 2026: Pull's cycle 2 on a plan whose Push day was
 * already missed that week). The save itself refuses no per-cycle override for being behind the
 * client; the stamp is the only rule that says whether one can still take effect.
 *
 * `consumedCycles` stays the answer where the read carried no stamp (an older server, a draft).
 */
export function cycleIsClosed(
  live: Pick<LiveMatrixInput, 'closedCycles'> | null,
  consumedCycles: number,
  cycleIndex: number,
): boolean {
  if (live?.closedCycles != null) return live.closedCycles.has(cycleIndex);
  return cycleIndex <= consumedCycles;
}

/**
 * The next cycle this exercise will be performed in: the first of its lifespan AFTER every cycle it
 * has a lived occurrence in, and still open to an edit.
 *
 * Owner's call, 15 September 2026. It was the plan-level `firstOpenCycle`, which put „TERAZ" over a
 * cycle whose cell for THIS exercise was already lived and locked (Push missed in cycle 2 while the
 * week's other days were still ahead), and which is null for every self-paced plan. Any row in the
 * record makes a cycle lived, including one the client is standing in, whose prescription froze at
 * start, so the marker always sits over a column a pin can still change.
 */
function nextCycleToPerform(
  executions: readonly SlotExecution[],
  lifespan: readonly number[],
  live: Pick<LiveMatrixInput, 'closedCycles'>,
  consumedCycles: number,
): number | null {
  const lastLived = Math.max(0, ...executions.map((execution) => execution.cycleIndex));
  return (
    lifespan.find(
      (cycle) => cycle > lastLived && !cycleIsClosed(live, consumedCycles, cycle),
    ) ?? null
  );
}

/**
 * The live variant's input, or `null` where there is no live variant to draw.
 *
 * `null` in and `null` out is the template reading: a library entry, an unassigned template, a fork
 * with no live assignment. An EMPTY list still answers — the client has trained nothing here yet,
 * but they exist, the now-marker belongs on the plan, and a cycle ahead of them is still ahead.
 */
export function executionsToMatrixInputs(source: LiveMatrixSource): LiveMatrixInput | null {
  if (source.executions === null) return null;

  const logged = new Map<string, LoggedCell>();
  const verdicts = new Map<number, CycleVerdict>();

  for (const execution of source.executions) {
    verdicts.set(execution.cycleIndex, verdictOf(execution));
    for (const set of execution.sets) {
      logged.set(loggedCellKey(execution.cycleIndex, set.setIndex), cellOf(set));
    }
  }

  const closedCycles = source.closedCycles === null ? null : new Set(source.closedCycles);
  return {
    nowCycle: nextCycleToPerform(
      source.executions,
      source.lifespan,
      { closedCycles },
      source.consumedCycles,
    ),
    consumedCycles: source.consumedCycles,
    closedCycles,
    logged,
    verdicts,
  };
}
